import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { pushDataLayer } from '@/lib/gtm';
import { useLanguage } from '@/context/LanguageContext';
import { useInquiryList } from '@/context/InquiryListContext';
import { translations } from '@/data/translations';
import { useProduct, useProductImages } from '@/hooks/useSupabase';
import { ImageGallery } from '@/components/ImageGallery';
import { ProductInquiryModal } from '@/components/ProductInquiryModal';
import { ProductDetailSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Send, Plus, Check } from 'lucide-react';

const pageTranslations = {
  requestAvailability: { de: 'Verfügbarkeit anfragen', en: 'Request availability', it: 'Richiedi disponibilità' },
  addToList: { de: '+ Zur Anfrageliste', en: '+ Add to inquiry list', it: '+ Aggiungi alla lista' },
  inList: { de: 'In der Anfrageliste', en: 'In inquiry list', it: 'Nella lista richieste' },
  sku: { de: 'Art.-Nr.', en: 'SKU', it: 'Cod.' }
};

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { t, language, getLocalizedField } = useLanguage();
  const { addItem, isInList } = useInquiryList();
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  const { product, loading } = useProduct(slug);
  const { images } = useProductImages(product?.id);

  const pt = (key) => pageTranslations[key]?.[language] || pageTranslations[key]?.en || key;

  // Must be before any early returns — hooks cannot be called conditionally
  const category = product?.category || null;

  useEffect(() => {
    if (!product) return;
    pushDataLayer({
      event: 'product_view',
      product_id: product.id,
      product_name: getLocalizedField(product, 'name'),
      product_sku: product.sku || undefined,
      product_price: product.price || undefined,
      product_currency: product.currency || 'EUR',
      product_category: category ? getLocalizedField(category, 'name') : undefined,
    });
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="py-12 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <ProductDetailSkeleton />
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="py-24 px-6 md:px-12 lg:px-24 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-[#2C2C2C] mb-4">
          {t(translations.notFound.title)}
        </h1>
        <p className="font-sans text-[#5D5D5D] mb-8">
          {t(translations.notFound.message)}
        </p>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/catalog">
            <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />
            {t(translations.product.backToCatalog)}
          </Link>
        </Button>
      </div>
    );
  }

  // Build images array - use fetched images or fallback to main image
  const allImages = images && images.length > 0 
    ? images 
    : product.image_url 
      ? [{ id: 'main', product_id: product.id, image_url: product.image_url, sort_order: 0 }]
      : [];

  const productInList = isInList(product.id);

  const handleAddToList = () => {
    addItem(product, 1);
    pushDataLayer({
      event: 'add_to_inquiry_list',
      product_id: product.id,
      product_name: getLocalizedField(product, 'name'),
      product_sku: product.sku || undefined,
      product_price: product.price || undefined,
      product_currency: product.currency || 'EUR',
      product_category: category ? getLocalizedField(category, 'name') : undefined,
    });
  };

  return (
    <div className="py-12 md:py-24 px-6 md:px-12 lg:px-24" data-testid="product-detail-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Back Button */}
        <Link
          to="/catalog"
          className="inline-flex items-center font-sans text-sm uppercase tracking-widest text-[#5D5D5D] hover:text-[#2C2C2C] transition-colors mb-8"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} />
          {t(translations.product.backToCatalog)}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <div>
            <ImageGallery 
              images={allImages} 
              productName={getLocalizedField(product, 'name')} 
            />
          </div>

          {/* Product Info */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            {/* Category */}
            {category && (
              <Link
                to={`/catalog?category=${category.slug}`}
                className="inline-block font-sans text-sm uppercase tracking-widest text-[#5D5D5D] hover:text-[#8B5A2B] transition-colors mb-4"
                data-testid="product-category"
              >
                {getLocalizedField(category, 'name')}
              </Link>
            )}

            {/* Name */}
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#2C2C2C] tracking-tight mb-4">
              {getLocalizedField(product, 'name')}
            </h1>

            {/* Price */}
            <div className="mb-6">
              {product.price && product.price < 2000 && (
                <p className="font-sans text-2xl text-[#2C2C2C]">
                  {product.price.toLocaleString('de-DE', { style: 'currency', currency: product.currency || 'EUR' })}
                </p>
              )}
              <p className="font-sans text-sm text-[#9CA3AF] mt-1 italic">
                {t(translations.product.priceNegotiable)}
              </p>
            </div>

            {/* Description */}
            {getLocalizedField(product, 'description') && (
              <p className="font-sans text-base text-[#5D5D5D] leading-relaxed mb-8">
                {getLocalizedField(product, 'description')}
              </p>
            )}

            <Separator className="my-8 bg-[#E5E5E5]" />

            {/* Details */}
            <div className="space-y-4 mb-8">
              {getLocalizedField(product, 'material') && (
                <div className="flex justify-between">
                  <span className="font-sans text-sm text-[#5D5D5D]">
                    {t(translations.product.material)}
                  </span>
                  <span className="font-sans text-sm text-[#2C2C2C]">
                    {getLocalizedField(product, 'material')}
                  </span>
                </div>
              )}
              {product.dimensions && (
                <div className="flex justify-between">
                  <span className="font-sans text-sm text-[#5D5D5D]">
                    {t(translations.product.dimensions)}
                  </span>
                  <span className="font-sans text-sm text-[#2C2C2C]">
                    {product.dimensions}
                  </span>
                </div>
              )}
              {product.sku && (
                <div className="flex justify-between">
                  <span className="font-sans text-sm text-[#5D5D5D]">
                    {pt('sku')}
                  </span>
                  <span className="font-sans text-sm text-[#2C2C2C]">
                    {product.sku}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Primary: Request Availability */}
              <Button
                onClick={() => setIsInquiryModalOpen(true)}
                className="w-full rounded-full bg-[#2C2C2C] text-white px-8 py-4 h-auto hover:bg-[#4A5D4F] transition-colors duration-300 text-sm uppercase tracking-widest"
                data-testid="request-availability-btn"
              >
                <Send className="mr-2 h-4 w-4" strokeWidth={1.5} />
                {pt('requestAvailability')}
              </Button>

              {/* Secondary: Add to Inquiry List */}
              <Button
                onClick={handleAddToList}
                variant="outline"
                disabled={productInList}
                className={`w-full rounded-full px-8 py-4 h-auto transition-colors duration-300 text-sm uppercase tracking-widest ${
                  productInList 
                    ? 'bg-[#4A5D4F]/10 border-[#4A5D4F] text-[#4A5D4F]' 
                    : 'border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white'
                }`}
                data-testid="add-to-list-btn"
              >
                {productInList ? (
                  <>
                    <Check className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {pt('inList')}
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    {pt('addToList')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Inquiry Modal */}
      <ProductInquiryModal
        product={product}
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
      />
    </div>
  );
}
